import styled, { css } from 'styled-components';

import { COLORS, MOBILE, SORTABLE_ANIM, STATUS_COLORS } from '../../../../theme/tokens';
import { Text } from '../../../../components/Text/Text';

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const Hint = styled(Text)`
  display: block;
  font-size: 12px;
  line-height: 1.5;
  color: ${COLORS.textFaint};
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* The lifted row is translated over its neighbours; without this the browser
     may still clip it against the scroll container the sheet puts it in. */
  position: relative;
`;

/**
 * The article being edited, called out by how it looks rather than by a label
 * next to its title: an accent rail down the left edge, a lit border and a
 * brighter surface. A word ("This article") competed with the titles for reading
 * attention on every row of the list; the rail is found at a glance and costs no
 * horizontal space.
 */
const CURRENT = css`
  border-color: ${COLORS.ctaBorder};
  background: ${COLORS.surfaceMuted};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 10px;
    bottom: 10px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: ${COLORS.ctaSolid};
  }
`;

export const Row = styled.li<{
  $dragged: boolean;
  $shift: number;
  $animated: boolean;
  $current: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  /* Uniform row height is not cosmetic: the drag maths converts a pointer delta
     into a number of rows, which only holds while every row is the same size. */
  height: 52px;
  padding: 0 12px;
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
  ${(p) => (p.$current ? CURRENT : null)}

  /* The lifted row rides above its neighbours and is scaled a touch, so the list
     reads as one row picked up out of a stack rather than as a swap in place. */
  z-index: ${(p) => (p.$dragged ? 2 : 1)};
  transform: translateY(${(p) => p.$shift}px)
    ${(p) => (p.$dragged ? `scale(${SORTABLE_ANIM.LIFT_SCALE})` : '')};
  box-shadow: ${(p) => (p.$dragged ? `0 14px 30px ${COLORS.pageBg}` : 'none')};

  /*
   * Only the row NOT under the finger eases. The dragged one is driven straight
   * from the pointer, and a transition there would make it trail behind the
   * cursor by the whole duration; it gets the easing back for the release, when
   * $animated flips on and it travels into the gap its neighbours opened.
   */
  transition: ${(p) =>
    p.$animated
      ? `transform ${p.$dragged ? SORTABLE_ANIM.SETTLE_MS : SORTABLE_ANIM.SHIFT_MS}ms ${SORTABLE_ANIM.EASE},
         box-shadow ${SORTABLE_ANIM.SETTLE_MS}ms ${SORTABLE_ANIM.EASE}`
      : 'none'};

  ${MOBILE} {
    height: 56px;
  }
`;

export const Handle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${COLORS.textFaint};
  cursor: grab;
  /* Claim the gesture from the scroller: without this a vertical drag on a phone
     scrolls the sheet instead of moving the row. */
  touch-action: none;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: ${COLORS.surfaceMuted};
    color: ${COLORS.text};
  }

  &:active {
    cursor: grabbing;
  }

  ${MOBILE} {
    width: 44px;
    height: 44px;
  }
`;

export const Position = styled(Text)<{ $current: boolean }>`
  flex: none;
  width: 20px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: ${(p) => (p.$current ? 700 : 400)};
  color: ${(p) => (p.$current ? COLORS.ctaSolid : COLORS.textFaint)};
`;

export const Titles = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

export const RowTitle = styled(Text)<{ $current: boolean }>`
  font-size: 14px;
  font-weight: ${(p) => (p.$current ? 600 : 400)};
  color: ${(p) => (p.$current ? COLORS.textStrong : COLORS.text)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Tag = styled(Text)<{ $tone: 'pillar' | 'draft' }>`
  flex: none;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(p) => (p.$tone === 'pillar' ? STATUS_COLORS.positive : COLORS.textFaint)};
`;

export const Empty = styled(Text)`
  display: block;
  font-size: 13px;
  color: ${COLORS.textMuted};
  padding: 6px 0;
`;
